/** "Recent" rows under the sidebar's destinations: the jobs the person opened last, one click back into each. */

import React from 'react';
import { SidebarMenuButton, SidebarMenuItem } from '../../shared/ui/sidebar';
import { cn } from '../../shared/ui/utils';
import { recentMonogram, type RecentItem } from '../../shared-ui/navigation/recentItems';

interface SidebarRecentGroupProps {
  items: RecentItem[];
  activeId?: string | null;
  /** `rail`: tile over label in the desktop main rail. `list`: a labelled row. */
  variant: 'rail' | 'list';
  /** Rail set to icons only: the tile carries the name in its tooltip. */
  iconsOnly?: boolean;
  rowClassName: string;
  iconWrapClassName: string;
  labelClassName: string;
  headingPadClassName: string;
  onOpen: (item: RecentItem) => void;
}

export function SidebarRecentGroup({
  items,
  activeId,
  variant,
  iconsOnly = false,
  rowClassName,
  iconWrapClassName,
  labelClassName,
  headingPadClassName,
  onOpen,
}: SidebarRecentGroupProps) {
  if (items.length === 0) return null;
  const rail = variant === 'rail';
  return (
    <>
      <SidebarMenuItem data-slot="app-sidebar-section-heading" data-recent-heading className="list-none group-data-[collapsible=icon]:hidden">
        <div
          data-slot="app-sidebar-section-label"
          className={cn(
            'font-apple mt-3 text-[10px] font-semibold uppercase tracking-wider text-nav-section-heading',
            rail ? 'text-center' : 'px-2',
            headingPadClassName,
          )}
          style={{ fontFamily: 'var(--font-apple)' }}
        >
          Recent
        </div>
      </SidebarMenuItem>
      {items.map((item) => {
        const active = item.id === activeId;
        return (
          <SidebarMenuItem key={`${item.kind}:${item.id}`} data-slot="app-sidebar-recent-item">
            <SidebarMenuButton
              data-id={`recent:${item.id}`}
              data-main-rail-item={rail || undefined}
              onClick={() => onOpen(item)}
              isActive={active}
              aria-current={active ? 'page' : undefined}
              aria-label={item.title}
              tooltip={{ children: item.title, side: 'right', sideOffset: 8, hidden: false }}
              className={rowClassName}
            >
              <div className={cn('relative w-[28px] shrink-0 flex items-center justify-center', iconWrapClassName)}>
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex size-[22px] items-center justify-center rounded-[6px] text-[10px] font-semibold leading-none tracking-[-0.02em]',
                    active ? 'bg-nav-item-active-text/15 text-nav-item-active-text' : 'bg-sidebar-foreground/10 text-nav-item-text group-hover:text-nav-item-text-hover',
                  )}
                >
                  {recentMonogram(item.title)}
                </span>
              </div>
              {!(rail && iconsOnly) && (
                <span
                  data-slot={rail ? 'main-rail-label' : undefined}
                  className={cn(
                    'font-apple min-w-0 flex-1 truncate font-medium group-data-[collapsible=icon]:hidden',
                    labelClassName,
                    active && 'text-nav-item-active-text font-semibold',
                  )}
                  style={{ fontFamily: 'var(--font-apple)' }}
                >
                  {item.title}
                </span>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </>
  );
}
